<template>
  <BottomSheetModal 
    v-model="isOpen" 
    :confirmDisabled="!isFormValid || (isEditing && !isDirty)"
    :hideHeader="currentView !== 'main'"
    @confirm="handleSave"
    @close="handleClose"
  >
    <template #title>{{ isEditing ? t('editTeam') : t('newTeam') }}</template>

    <div class="views-container">
      <!-- Main View -->
      <div class="main-view" :class="{ 'main-view--hidden': currentView !== 'main' }">
        <!-- Team Name Section -->
        <IosSection form :title="t('teamName')" required>
          <IosInput 
            v-model="form.name" 
            :placeholder="t('teamNamePlaceholder')"
            :maxlength="24"
            first
            last
          />
        </IosSection>

        <!-- Settings Section -->
        <IosSection form :title="t('settings')">
          <IosRow 
            :label="t('location')" 
            :value="form.location?.label || ''"
            clickable
            required
            first
            @click="currentView = 'location'"
          />
          
          <IosRow 
            :label="t('teamLogo')" 
            clickable
            @click="currentView = 'logo'"
          >
            <template #value>
              <div class="logo-preview">
                <img :src="logoPreview || defaultLogoUrl" alt="Logo" />
              </div>
            </template>
          </IosRow>
          
          <IosSwitch 
            v-model="form.isPrivate" 
            :label="t('privateTeam')"
          />

          <IosSwitch 
            v-model="form.recruitment" 
            :label="t('recruitment')"
          />

          <IosRow 
            :label="t('ageGroup')" 
            :value="t(`ageGroup_${form.ageGroup}`)"
            clickable
            @click="currentView = 'ageGroup'"
          />

          <IosRow 
            :label="t('bankAccount')"
            :value="bankAccountDisplay"
            clickable
            last
            @click="currentView = 'bankAccount'"
          />
        </IosSection>

        <!-- Colors Section -->
        <IosSection form :title="t('teamColors')">
          <IosRow 
            :label="t('primaryColor')" 
            clickable
            @click="openColorPicker('primary')"
          >
            <template #value>
              <div class="color-preview" :style="{ background: form.primaryColor }"></div>
            </template>
          </IosRow>
          
          <IosRow 
            :label="t('secondaryColor')" 
            clickable
            last
            @click="openColorPicker('secondary')"
          >
            <template #value>
              <div class="color-preview" :style="{ background: form.secondaryColor }"></div>
            </template>
          </IosRow>
        </IosSection>

        <!-- Jersey Colors Section -->
        <IosSection form :title="t('jerseyColors')" required>
          <IosRow 
            :label="t('selectJerseyColors')" 
            clickable
            last
            noPadding
            @click="currentView = 'jerseys'"
          >
            <template #value>
              <div v-if="form.dressColors.length" class="jersey-stack">
                <div 
                  v-for="(color, index) in form.dressColors.slice(0, 5)" 
                  :key="color"
                  class="jersey-stack-item"
                  :style="{ zIndex: form.dressColors.length - index }"
                >
                  <img :src="getDressImage(color)" :alt="color" />
                </div>
                <span v-if="form.dressColors.length > 5" class="jersey-stack-more">
                  +{{ form.dressColors.length - 5 }}
                </span>
              </div>
            </template>
          </IosRow>
        </IosSection>

        <!-- Role Sections -->
        <RoleSection
          v-for="roleCfg in formRoles"
          :key="roleCfg.key"
          :users="form[roleCfg.key]"
          :title="t(roleCfg.titleKey)"
          :role-label="t(roleCfg.labelKey)"
          :add-label="t(roleCfg.addLabelKey)"
          :editable="true"
          :min-count="roleCfg.minCount"
          :required="roleCfg.key === 'admins'"
          @add="openInviteModal(roleCfg.singular)"
          @remove="(index) => removeFromRole(roleCfg.key, index)"
          @click-user="(user) => openPersonDetail(user, roleCfg.singular)"
        />

        <!-- Delete Team (only in edit mode) -->
        <div v-if="isEditing" class="delete-org-section">
          <button class="delete-org-btn" @click="showDeleteTeamConfirm = true; deleteConfirmInput = ''">
            {{ $t('deleteTeam') }}
          </button>
        </div>
      </div>

      <!-- Location Search View -->
      <Transition name="slide">
        <div v-if="currentView === 'location'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ t('selectLocation') }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <IosSection form>
              <div class="search-input-wrapper-ios">
                <v-icon size="20" class="search-icon">mdi-magnify</v-icon>
                <input 
                  v-model="locationSearch"
                  type="text"
                  class="search-input"
                  :placeholder="t('searchCity')"
                  @input="onLocationSearch"
                />
                <v-progress-circular v-if="searchingCities" indeterminate size="18" width="2" class="ml-2" />
              </div>
            </IosSection>
        
            <IosSection form v-if="cityResults.length > 0">
              <IosRow 
                v-for="(city, index) in cityResults" 
                :key="city.id"
                :label="city.label"
                :subtitle="city.subtitle"
                :first="index === 0"
                :last="index === cityResults.length - 1"
                clickable
                @click="selectLocation(city)"
              />
            </IosSection>
            
            <div v-else-if="!cityResults.length && locationSearch.length >= 2 && !searchingCities" class="search-empty">
              {{ t('noResults') }}
            </div>
          </div>
        </div>
      </Transition>

      <!-- Logo Selection View -->
      <Transition name="slide">
        <div v-if="currentView === 'logo'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ t('teamLogo') }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <div class="logo-section">
            <div class="current-logo">
              <img :src="logoPreview || defaultLogoUrl" alt="Logo" />
            </div>
        
            <div class="logo-actions">
              <v-btn 
                variant="tonal" 
                color="primary"
                @click="selectLogo"
              >
                <v-icon start>mdi-image</v-icon>
                {{ t('selectImage') }}
              </v-btn>
          
              <v-btn 
                v-if="logoPreview"
                variant="text" 
                color="error"
                @click="removeLogo"
              >
                {{ t('remove') }}
              </v-btn>
            </div>
        
              <input 
                ref="logoInput"
                type="file"
                accept="image/*"
                class="hidden-input"
                @change="onLogoSelected"
              />
            </div>
          </div>
        </div>
      </Transition>

      <!-- Color Picker View -->
      <Transition name="slide">
        <div v-if="currentView === 'colorPicker'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">
              {{ editingColor === 'primary' ? t('primaryColor') : t('secondaryColor') }}
            </div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <div class="color-picker-section">
            <v-color-picker 
              v-model="currentColorValue"
              :modes="['hex', 'rgb']"
              show-swatches
              swatches-max-height="200px"
              width="100%"
              theme="dark"
              class="color-picker"
            />
            </div>
          </div>
        </div>
      </Transition>

      <!-- Age Group View -->
      <Transition name="slide">
        <div v-if="currentView === 'ageGroup'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ t('selectAgeGroup') }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <IosSection form>
              <IosRow 
                v-for="(group, index) in ageGroupOptions" 
                :key="group.value"
                :label="group.label"
                :first="index === 0"
                :last="index === ageGroupOptions.length - 1"
                clickable
                :show-chevron="false"
                @click="selectAgeGroup(group.value)"
              >
                <template #value>
                  <v-icon 
                    v-if="form.ageGroup === group.value" 
                    size="20" 
                    color="primary"
                  >mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Jersey Colors View -->
      <Transition name="slide">
        <div v-if="currentView === 'jerseys'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ t('jerseyColors') }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <div class="jerseys-grid">
            <div 
              v-for="color in availableDressColors" 
              :key="color"
              class="jersey-item"
              :class="{ 'jersey-item--selected': form.dressColors.includes(color) }"
              @click="toggleDressColor(color)"
            >
              <img :src="getDressImage(color)" :alt="color" />
              <div class="jersey-label">{{ color.replace('-', ' / ').toUpperCase() }}</div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
      <!-- Bank Account View -->
      <Transition name="slide">
        <div v-if="currentView === 'bankAccount'" class="subview">
          <BankAccountPicker
            v-model="bankAccountValue"
            @back="currentView = 'main'"
          />
        </div>
      </Transition>
    </div>

    <!-- Invite Persons Modal -->
    <InvitePersonsModal
      v-model="showInviteModal"
      :title="inviteModalTitle"
      :confirm-button-label="t('invite.add')"
      :already-selected="allSelectedUsers"
      :entity-id="props.teamId || ''"
      entity-type="team"
      :role="inviteRole"
      @done="handleInviteDone"
    />

    <PlayerCardModal ref="playerCardRef" />

    <!-- Delete Team Confirm -->
    <CenteredModal v-model="showDeleteTeamConfirm" :show-close-button="true" max-width="350px">
      <div class="delete-confirm-dialog">
        <div class="delete-confirm-icon">
          <v-icon size="48" color="error">mdi-delete-alert</v-icon>
        </div>
        <h3 class="delete-confirm-title">{{ $t('deleteTeam') }}</h3>
        <p class="delete-confirm-subtitle">
          {{ $t('deleteTeamConfirm', { name: form.name }) }}
        </p>
        <div class="delete-confirm-input-wrap">
          <input
            v-model="deleteConfirmInput"
            class="delete-confirm-input"
            :placeholder="$t('typeToConfirmDelete', { word: $t('delete').toLowerCase() })"
            autocomplete="off"
          />
        </div>
        <div class="delete-confirm-actions">
          <button class="delete-confirm-btn delete-confirm-btn--cancel" @click="cancelDeleteTeam">
            {{ $t('cancel') }}
          </button>
          <button 
            class="delete-confirm-btn delete-confirm-btn--confirm" 
            :disabled="!isDeleteConfirmValid"
            @click="executeDeleteTeam"
          >
            {{ $t('delete') }}
          </button>
        </div>
      </div>
    </CenteredModal>
  </BottomSheetModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppStore } from '@/stores/app/appStore';
import { useUserStore } from '@/stores/user/userStore';
import { getDressImage, getTeamImage, getUserAvatarUrl } from '@/utils/imageUtils';
import BottomSheetModal from '@/components/BottomSheetModal.vue';
import InvitePersonsModal from '@/components/InvitePersonsModal.vue';
import BankAccountPicker from '@/components/BankAccountPicker.vue';
import RoleSection from '@/components/Team/RoleSection.vue';
import PlayerCardModal from '@/components/PlayerCardModal.vue';
import CenteredModal from '@/components/CenteredModal.vue';
import { IosSection, IosRow, IosInput, IosSwitch } from '@/components/ios';
import axiosInstance from '@/services/axiosInstance';
import { TEAM_ROLES, SINGULAR_TO_PLURAL, mapUserLite } from './teamRoles';
import type { UserLite, TeamRoleKey, TeamRoleSingular, TeamRoleConfig } from './teamRoles';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

const { t } = useI18n({ useScope: 'global' });
const appStore = useAppStore();
const userStore = useUserStore();

type ViewType = 'main' | 'location' | 'logo' | 'colorPicker' | 'jerseys' | 'ageGroup' | 'bankAccount';

interface LocationOption {
  id: string | number;
  label: string;
  subtitle?: string;
  lat?: string;
  lon?: string;
}

interface TeamForm {
  name: string;
  location: LocationOption | null;
  logoFile: File | null;
  logoUrl: string;
  isPrivate: boolean;
  primaryColor: string;
  secondaryColor: string;
  dressColors: string[];
  admins: UserLite[];
  coaches: UserLite[];
  players: UserLite[];
  goalkeepers: UserLite[];
  externalOrganizers: UserLite[];
  treasurers: UserLite[];
  captains: UserLite[];
  timekeepers: UserLite[];
  ageGroup: string;
  recruitment: boolean;
  bankAccountPrefix: string;
  bankAccountNumber: string;
  bankCode: string;
}

/** Which roles to show in the team form (functional roles, not player/goalkeeper) */
const formRoles: TeamRoleConfig[] = [
  TEAM_ROLES.admins,
  TEAM_ROLES.coaches,
  TEAM_ROLES.externalOrganizers,
  TEAM_ROLES.treasurers,
  TEAM_ROLES.captains,
  TEAM_ROLES.timekeepers,
];

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  teamId: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['update:modelValue', 'saved', 'deleted']);

// State
const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const isEditing = computed(() => !!props.teamId);
const currentView = ref<ViewType>('main');

const form = ref<TeamForm>({
  name: '',
  location: null,
  logoFile: null,
  logoUrl: '',
  isPrivate: false,
  primaryColor: '#1a237e',
  secondaryColor: '#ffc107',
  dressColors: [],
  admins: [],
  coaches: [],
  players: [],
  goalkeepers: [],
  externalOrganizers: [],
  treasurers: [],
  captains: [],
  timekeepers: [],
  ageGroup: 'seniors',
  recruitment: false,
  bankAccountPrefix: '',
  bankAccountNumber: '',
  bankCode: ''
});

// Bank account
const bankAccountValue = computed({
  get: () => ({
    prefix: form.value.bankAccountPrefix,
    number: form.value.bankAccountNumber,
    bankCode: form.value.bankCode,
  }),
  set: (val: { prefix: string; number: string; bankCode: string }) => {
    form.value.bankAccountPrefix = val.prefix;
    form.value.bankAccountNumber = val.number;
    form.value.bankCode = val.bankCode;
  },
});

const bankAccountDisplay = computed(() => {
  const number = form.value.bankAccountNumber;
  const code = form.value.bankCode;
  if (!number || !code) return t('notSet');
  const lastDigits = number.slice(-3);
  return `...${lastDigits}/${code}`;
});

// Location search
const locationSearch = ref('');
const cityResults = ref<LocationOption[]>([]);
const searchingCities = ref(false);
let locationSearchTimeout: ReturnType<typeof setTimeout> | null = null;

// Logo
const logoInput = ref<HTMLInputElement | null>(null);
const defaultLogoUrl = computed(() => getTeamImage('default-team-logo.png', 'w150'));
const logoPreview = computed(() => {
  if (form.value.logoFile) {
    return URL.createObjectURL(form.value.logoFile);
  }
  if (form.value.logoUrl) {
    if (form.value.logoUrl.startsWith('http')) return form.value.logoUrl;
    return getTeamImage(form.value.logoUrl, 'w150');
  }
  return '';
});

// Colors
const editingColor = ref<'primary' | 'secondary'>('primary');
const currentColorValue = computed({
  get: () => editingColor.value === 'primary' ? form.value.primaryColor : form.value.secondaryColor,
  set: (val) => {
    if (editingColor.value === 'primary') {
      form.value.primaryColor = val;
    } else {
      form.value.secondaryColor = val;
    }
  }
});

// Jerseys
const availableDressColors = computed(() => appStore.dressColors || []);

// Age Group
const ageGroupOptions = computed(() => [
  { value: 'preschool', label: t('ageGroup_preschool') },
  { value: 'youngerJuniors', label: t('ageGroup_youngerJuniors') },
  { value: 'olderJuniors', label: t('ageGroup_olderJuniors') },
  { value: 'women', label: t('ageGroup_women') },
  { value: 'youth', label: t('ageGroup_youth') },
  { value: 'juniors', label: t('ageGroup_juniors') },
  { value: 'seniorsUnregistered', label: t('ageGroup_seniorsUnregistered') },
  { value: 'seniors', label: t('ageGroup_seniors') },
]);

const selectAgeGroup = (value: string) => {
  form.value.ageGroup = value;
  currentView.value = 'main';
};

// Invite Modal
const showInviteModal = ref(false);
const inviteRole = ref<TeamRoleKey>('admins');
const invitingForRole = ref<TeamRoleSingular>('admin');
const playerCardRef = ref<InstanceType<typeof PlayerCardModal>>();

const inviteModalTitle = computed(() => {
  const cfg = TEAM_ROLES[inviteRole.value];
  return cfg ? t(cfg.addLabelKey) : t('invitePeople');
});

// Only exclude users already in the same role (allow same person in multiple roles)
const allSelectedUsers = computed(() => {
  return form.value[inviteRole.value] || [];
});

// Dirty tracking for edit mode
const originalFormSnapshot = ref('');

const isDirty = computed(() => {
  if (!originalFormSnapshot.value) return false;
  const { logoFile, ...rest } = form.value;
  return JSON.stringify(rest) !== originalFormSnapshot.value || logoFile !== null;
});

// Validation
const isFormValid = computed(() => {
  return form.value.name.trim().length >= 2 && 
         form.value.name.trim().length <= 24 &&
         form.value.location !== null &&
         form.value.dressColors.length > 0 &&
         form.value.admins.length > 0;
});

// Methods
const onLocationSearch = () => {
  if (locationSearchTimeout) clearTimeout(locationSearchTimeout);
  
  locationSearchTimeout = setTimeout(() => {
    if (!locationSearch.value || locationSearch.value.length < 2) {
      cityResults.value = [];
      return;
    }
    searchCities(locationSearch.value.trim());
  }, 300);
};

const searchCities = async (query: string) => {
  searchingCities.value = true;
  try {
    const url = new URL('https://nominatim.openstreetmap.org/search');
    url.searchParams.set('q', query);
    url.searchParams.set('format', 'jsonv2');
    url.searchParams.set('addressdetails', '1');
    url.searchParams.set('limit', '10');
    url.searchParams.set('featuretype', 'city');

    const res = await fetch(url.toString(), {
      headers: { 'Accept': 'application/json' },
    });
    const data = await res.json();

    const mapped: LocationOption[] = (Array.isArray(data) ? data : []).map((r: any) => {
      const addr = r.address || {};
      const cityLike = addr.city || addr.town || addr.village || r.display_name?.split(',')?.[0] || r.name;
      const region = addr.state || addr.region || addr.county;
      const country = addr.country || t('countries.defaultCountry');
      return {
        id: Number(r.place_id),
        label: String(cityLike),
        subtitle: [region, country].filter(Boolean).join(', '),
        lat: r.lat,
        lon: r.lon,
      };
    });

    // Remove duplicates
    const seen = new Set<string>();
    cityResults.value = mapped.filter(m => {
      const key = m.label.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  } catch (e) {
    console.error('City search failed:', e);
    cityResults.value = [];
  } finally {
    searchingCities.value = false;
  }
};

const selectLocation = (city: LocationOption) => {
  form.value.location = city;
  currentView.value = 'main';
};

const selectLogo = () => {
  logoInput.value?.click();
};

const onLogoSelected = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    form.value.logoFile = file;
  }
};

const removeLogo = () => {
  form.value.logoFile = null;
  form.value.logoUrl = '';
};

const openColorPicker = (type: 'primary' | 'secondary') => {
  editingColor.value = type;
  currentView.value = 'colorPicker';
};

const toggleDressColor = (color: string) => {
  const index = form.value.dressColors.indexOf(color);
  if (index > -1) {
    form.value.dressColors.splice(index, 1);
  } else {
    form.value.dressColors.push(color);
  }
};

const openPersonDetail = (person: UserLite, role: string) => {
  playerCardRef.value?.open(person);
};

const openInviteModal = (role: TeamRoleSingular) => {
  invitingForRole.value = role;
  inviteRole.value = SINGULAR_TO_PLURAL[role];
  showInviteModal.value = true;
};

const handleInviteDone = (users: UserLite[]) => {
  const targetArray = form.value[inviteRole.value];
  for (const user of users) {
    if (!targetArray.some((u: UserLite) => u._id === user._id)) {
      targetArray.push(user);
    }
  }
  showInviteModal.value = false;
};

const removeFromRole = (roleKey: TeamRoleKey, index: number) => {
  form.value[roleKey].splice(index, 1);
};

// Saving state
const isSaving = ref(false);

const handleSave = async () => {
  if (isSaving.value) return;
  isSaving.value = true;
  
  try {
    // Determine imageUrl - if no logo file and no existing logoUrl, use default
    const imageUrl = form.value.logoUrl || (form.value.logoFile ? undefined : 'default-team-logo.png');
    
    const teamData = {
      name: form.value.name.trim(),
      location: form.value.location ? {
        label: form.value.location.label,
        city: form.value.location.label,
        lat: form.value.location.lat,
        lon: form.value.location.lon,
        subtitle: form.value.location.subtitle,
      } : null,
      isPrivate: form.value.isPrivate,
      primaryTeamColor: form.value.primaryColor,
      secondaryTeamColor: form.value.secondaryColor,
      colors: form.value.dressColors,
      admins: form.value.admins.map(u => u._id),
      coaches: form.value.coaches.map(u => u._id),
      players: form.value.players.map(u => u._id),
      goalkeepers: form.value.goalkeepers.map(u => u._id),
      externalOrganizers: form.value.externalOrganizers.map(u => u._id),
      treasurers: form.value.treasurers.map(u => u._id),
      captains: form.value.captains.map(u => u._id),
      timekeepers: form.value.timekeepers.map(u => u._id),
      imageUrl,
      ageGroup: form.value.ageGroup,
      recruitment: form.value.recruitment,
      bankAccount: (form.value.bankAccountPrefix || form.value.bankAccountNumber || form.value.bankCode) ? {
        accountPrefix: form.value.bankAccountPrefix,
        accountNumber: form.value.bankAccountNumber,
        bankCode: form.value.bankCode,
      } : null,
    };

    let savedTeamId: string;

    if (isEditing.value && props.teamId) {
      // Update existing team
      await axiosInstance.put(`${API_BASE_URL}/teams/${props.teamId}`, teamData);
      savedTeamId = props.teamId;
    } else {
      // Create new team
      const response = await axiosInstance.post(`${API_BASE_URL}/teams`, teamData);
      savedTeamId = response.data.article._id;
    }

    // Upload logo if changed
    if (form.value.logoFile) {
      const formData = new FormData();
      formData.append('image', form.value.logoFile);
      await axiosInstance.post(`${API_BASE_URL}/teams/${savedTeamId}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }

    emit('saved', { ...form.value, _id: savedTeamId });
    isOpen.value = false;
  } catch (error) {
    console.error('Error saving team:', error);
    // TODO: Show error notification
  } finally {
    isSaving.value = false;
  }
};

const handleClose = () => {
  currentView.value = 'main';
};

// Delete team with text confirmation
const showDeleteTeamConfirm = ref(false);
const deleteConfirmInput = ref('');

const isDeleteConfirmValid = computed(() => 
  deleteConfirmInput.value.toLowerCase() === t('delete').toLowerCase()
);

const cancelDeleteTeam = () => {
  showDeleteTeamConfirm.value = false;
  deleteConfirmInput.value = '';
};

const executeDeleteTeam = async () => {
  if (!isDeleteConfirmValid.value || !props.teamId) return;
  showDeleteTeamConfirm.value = false;
  deleteConfirmInput.value = '';
  try {
    await axiosInstance.delete(`/teams/${props.teamId}`);
    isOpen.value = false;
    emit('deleted', props.teamId);
  } catch (error) {
    console.error('Error deleting team:', error);
  }
};

// Load team data for editing
const loadTeamData = async () => {
  if (!props.teamId) return;
  
  try {
    const { data: team } = await axiosInstance.get(`${API_BASE_URL}/teams/${props.teamId}`);
    
    // Parse location — can be string or object { label, city, lat, lon, subtitle }
    let parsedLocation: LocationOption | null = null;
    if (team.location) {
      if (typeof team.location === 'string') {
        parsedLocation = { id: 'loaded', label: team.location };
      } else if (typeof team.location === 'object') {
        parsedLocation = {
          id: 'loaded',
          label: team.location.label || team.location.city || '',
          subtitle: team.location.subtitle,
          lat: team.location.lat,
          lon: team.location.lon,
        };
      }
    }

    form.value = {
      name: team.name || '',
      location: parsedLocation,
      logoFile: null,
      logoUrl: team.imageUrl || '',
      isPrivate: team.isPrivate || false,
      primaryColor: team.primaryTeamColor || '#1a237e',
      secondaryColor: team.secondaryTeamColor || '#ffc107',
      dressColors: team.colors || [],
      admins: (team.admins || []).map(mapUserLite),
      coaches: (team.coaches || []).map(mapUserLite),
      players: (team.players || []).map(mapUserLite),
      goalkeepers: (team.goalkeepers || []).map(mapUserLite),
      externalOrganizers: (team.externalOrganizers || []).map(mapUserLite),
      treasurers: (team.treasurers || []).map(mapUserLite),
      captains: (team.captains || []).map(mapUserLite),
      timekeepers: (team.timekeepers || []).map(mapUserLite),
      ageGroup: team.ageGroup || 'seniors',
      recruitment: team.recruitment || false,
      bankAccountPrefix: team.bankAccount?.accountPrefix || '',
      bankAccountNumber: team.bankAccount?.accountNumber || '',
      bankCode: team.bankAccount?.bankCode || ''
    };
  } catch (error) {
    console.error('Error loading team:', error);
  }
};

// Add current user as admin for new teams
const addCurrentUserAsAdmin = () => {
  const user = userStore.user;
  if (user && form.value.admins.length === 0) {
    form.value.admins.push({
      _id: user._id,
      firstName: user.firstName,
      lastName: user.lastName,
      name: user.name,
      nickName: user.nickName,
      email: user.email,
      avatar: getUserAvatarUrl(user) || undefined
    });
  }
};

// Reset form when modal opens
watch(isOpen, async (val) => {
  if (val) {
    currentView.value = 'main';
    if (props.teamId) {
      // Load existing team data
      await loadTeamData();
      const { logoFile, ...rest } = form.value;
      originalFormSnapshot.value = JSON.stringify(rest);
    } else {
      // Reset form for new team
      form.value = {
        name: '',
        location: null,
        logoFile: null,
        logoUrl: '',
        isPrivate: false,
        primaryColor: '#1a237e',
        secondaryColor: '#ffc107',
        dressColors: [],
        admins: [],
        coaches: [],
        players: [],
        goalkeepers: [],
        externalOrganizers: [],
        treasurers: [],
        captains: [],
        timekeepers: [],
        ageGroup: 'seniors',
        recruitment: false,
        bankAccountPrefix: '',
        bankAccountNumber: '',
        bankCode: ''
      };
      // Add current user as admin for new teams
      addCurrentUserAsAdmin();
    }
    // Clear search states
    locationSearch.value = '';
    cityResults.value = [];
    showInviteModal.value = false;
  }
});
</script>

<style lang="scss" scoped>
// Views container
.views-container {
  position: relative;
  overflow: hidden;
  min-height: calc(92vh - 90px);
}

.main-view {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1), 
              opacity 0.25s ease;
  
  &--hidden {
    opacity: 0.3;
    transform: translateX(-100px) scale(0.92);
    pointer-events: none;
  }
}

// Slide transition - iOS style
.slide-enter-active {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
}

.slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}

.slide-enter-from {
  transform: translateX(100%) !important;
}

.slide-leave-to {
  transform: translateX(100%) !important;
}

.subview {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  max-width: 600px;
  height: 92vh;
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-sheetBackground));
  border-radius: 32px 32px 0 0;
  z-index: 10000;
  overflow: hidden;
  transform: translateX(0);
}

.subview-header {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  z-index: 10;
  pointer-events: none;
  
  > * {
    pointer-events: auto;
  }
}

.subview-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 20px;
  padding-top: 70px; // space for fixed header
}

.subview-content {
  flex: 1;
}

.back-btn {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: rgb(var(--v-theme-sheetCloseBtn));
  cursor: pointer;
  transition: all 0.2s ease;
  
  :deep(.v-icon) {
    color: rgb(var(--v-theme-sheetText)) !important;
  }
  
  &:hover {
    filter: brightness(0.92);
  }
  
  &:active {
    transform: scale(0.95);
  }
}

.close-btn-right {
  @extend .back-btn;
  margin-left: auto;
}

.subview-title {
  flex: 1;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
}

.spacer {
  width: 45px;
}

// Search
.search-container {
  padding: 16px;
}

.search-container-inline {
  padding: 12px 16px;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  background: rgba(var(--v-theme-sheetText), 0.1);
  border-radius: 24px;
  padding: 12px 16px;
}

.search-input-wrapper-ios {
  display: flex;
  align-items: center;
  padding: 0 16px;
  min-height: 48px;
  
  .search-icon {
    color: rgba(var(--v-theme-sheetText), 0.5);
    margin-right: 12px;
  }
  
  .search-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    font-size: 1rem;
    color: rgb(var(--v-theme-sheetText));
    
    &::placeholder {
      color: rgba(var(--v-theme-sheetText), 0.4);
    }
  }
}

.search-icon {
  color: rgba(var(--v-theme-sheetText), 0.5);
  margin-right: 12px;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 1rem;
  color: rgb(var(--v-theme-sheetText));
  
  &::placeholder {
    color: rgba(var(--v-theme-sheetText), 0.4);
  }
}

.search-results {
  padding: 0 16px;
}

.search-results-inline {
  max-height: 300px;
  overflow-y: auto;
}

.search-result-item {
  padding: 12px 0;
  border-bottom: 1px solid rgba(var(--v-theme-sheetText), 0.1);
  cursor: pointer;
  
  &:active {
    background: rgba(var(--v-theme-sheetText), 0.08);
  }
}

.result-label {
  color: rgb(var(--v-theme-sheetText));
  font-size: 1rem;
}

.result-subtitle {
  color: rgba(var(--v-theme-sheetText), 0.5);
  font-size: 0.85rem;
  margin-top: 2px;
}

.search-loading,
.search-empty {
  padding: 24px;
  text-align: center;
  color: rgba(var(--v-theme-sheetText), 0.5);
}

.search-empty-inline {
  padding: 16px;
  text-align: center;
  color: rgba(var(--v-theme-sheetText), 0.5);
  font-size: 0.9rem;
}

// Logo
.logo-section {
  padding: 32px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.current-logo {
  width: 120px;
  height: 120px;
  border-radius: 32px;
  background: rgba(var(--v-theme-sheetText), 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.logo-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.logo-preview {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.hidden-input {
  display: none;
}

// Colors
.color-preview {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid rgba(var(--v-theme-sheetText), 0.25);
}

.color-picker-section {
  padding: 16px;
}

.color-picker {
  background: transparent !important;
}

// Jerseys
.jerseys-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 16px;
}

.jersey-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  background: rgba(var(--v-theme-sheetText), 0.08);
  border-radius: 20px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &--selected {
    border-color: rgb(var(--v-theme-primary));
    background: rgba(var(--v-theme-primary), 0.15);
  }
  
  img {
    width: 80px;
    height: 60px;
    object-fit: contain;
    margin-bottom: 8px;
  }
}

.jersey-label {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-sheetText), 0.7);
  text-align: center;
}

// Jersey stack for row preview
.jersey-stack {
  display: flex;
  align-items: center;
}

.jersey-stack-item {
  width: 56px;
  height: 44px;
  margin-left: -30px;
  
  &:first-child {
    margin-left: 0;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.jersey-stack-more {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-sheetText), 0.5);
  margin-left: 6px;
}

// Invite
.invite-actions {
  padding: 16px;
  margin-top: 16px;
}

// Phone input
.phone-input-row {
  display: flex;
  align-items: center;
  padding: 0 16px;
  min-height: 48px;
  gap: 12px;
}

.country-select {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(var(--v-theme-sheetText), 0.1);
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.2s;
  
  &:active {
    background: rgba(var(--v-theme-sheetText), 0.15);
  }
}

.country-flag {
  font-size: 1.2rem;
}

.country-code {
  color: rgb(var(--v-theme-sheetText));
  font-size: 0.9rem;
}

.phone-number-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 1rem;
  color: rgb(var(--v-theme-sheetText));
  
  &::placeholder {
    color: rgba(var(--v-theme-sheetText), 0.4);
  }
}

.country-picker {
  margin: 8px 16px;
  background: rgba(var(--v-theme-sheetText), 0.08);
  border-radius: 16px;
  overflow: hidden;
}

.country-option {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
  cursor: pointer;
  border-bottom: 1px solid rgba(var(--v-theme-sheetText), 0.1);
  transition: background 0.15s;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:active {
    background: rgba(var(--v-theme-sheetText), 0.12);
  }
  
  .country-name {
    flex: 1;
    color: rgb(var(--v-theme-sheetText));
  }
  
  .country-code {
    color: rgba(var(--v-theme-sheetText), 0.6);
  }
}

// Role select
.role-select-content {
  padding: 24px 16px;
}

.role-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
  margin-bottom: 8px;
}

.role-subtitle {
  color: rgba(var(--v-theme-sheetText), 0.6);
  margin-bottom: 24px;
}

// Text helpers
.text-disabled {
  color: rgba(var(--v-theme-sheetText), 0.4);
}

.delete-org-section {
  padding: 32px 16px 16px;
  text-align: center;
}

.delete-org-btn {
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: none;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  color: rgb(var(--v-theme-error));
  background: rgba(var(--v-theme-error), 0.1);
  transition: all 0.2s ease;

  &:active {
    transform: scale(0.97);
  }
}

.delete-confirm-dialog {
  padding: 24px;
  text-align: center;
}

.delete-confirm-icon {
  margin-bottom: 16px;
}

.delete-confirm-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.delete-confirm-subtitle {
  font-size: 0.9rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin-bottom: 16px;
}

.delete-confirm-input-wrap {
  margin-bottom: 20px;
}

.delete-confirm-input {
  width: 100%;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.15);
  font-size: 0.95rem;
  text-align: center;
  background: transparent;
  color: inherit;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: rgb(var(--v-theme-error));
  }
}

.delete-confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.delete-confirm-btn {
  padding: 10px 24px;
  border-radius: 12px;
  border: none;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &--cancel {
    background: rgba(var(--v-theme-on-surface), 0.1);
    color: inherit;
  }

  &--confirm {
    background: rgb(var(--v-theme-error));
    color: white;

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }
}

</style>
